// Pieces shared by the stage and USGS charts: container width, the active
// (hovered or focused) index, axes, the tooltip and the table-view twin.
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX, KeyboardEvent, PointerEvent, ReactNode } from 'react';
import { formatDateTime } from '../../lib/format';
import type { LinearScale } from './chartMath';
import { formatTimeTick } from './chartMath';

export const MARGIN = { top: 20, right: 14, bottom: 26, left: 48 } as const;
const FALLBACK_WIDTH = 340;

export function useContainerWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width: width > 0 ? Math.round(width) : FALLBACK_WIDTH };
}

/**
 * Hovered or keyboard-focused data index. `fallback` is where the arrow keys
 * start from when nothing is active; the index clamps when the data shrinks.
 */
export function useActiveIndex(count: number, fallback: number) {
  const [active, setActive] = useState<number | null>(null);
  const index = active != null && active >= 0 && active < count ? active : null;
  const onKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
    if (count === 0) return;
    const current = index ?? Math.min(Math.max(0, fallback), count - 1);
    let next: number | null = current;
    switch (e.key) {
      case 'ArrowRight':
        next = Math.min(count - 1, current + 1);
        break;
      case 'ArrowLeft':
        next = Math.max(0, current - 1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      case 'Escape':
        next = null;
        break;
      default:
        return;
    }
    e.preventDefault();
    setActive(next);
  };
  return { index, setActive, onKeyDown };
}

/** Pointer position in SVG user units (the SVG is drawn at its measured width, so 1:1 up to rounding). */
export function pointerX(e: PointerEvent<SVGSVGElement>, width: number): number {
  const rect = e.currentTarget.getBoundingClientRect();
  return rect.width ? ((e.clientX - rect.left) / rect.width) * width : 0;
}

export function YGrid({ ticks, y, x0, x1, format }: { ticks: number[]; y: LinearScale; x0: number; x1: number; format: (v: number) => string }): JSX.Element {
  return (
    <g>
      {ticks.map((tick) => {
        const py = y(tick);
        return (
          <g key={`y-${tick}`}>
            <line className="gd-grid" x1={x0} x2={x1} y1={py} y2={py} />
            <text className="gd-axis-text" x={x0 - 6} y={py + 3.5} textAnchor="end">
              {format(tick)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

export function XAxis({
  ticks,
  stepMs,
  x,
  x0,
  x1,
  yTop,
  yBottom,
  height,
}: {
  ticks: number[];
  stepMs: number;
  x: LinearScale;
  x0: number;
  x1: number;
  yTop: number;
  yBottom: number;
  height: number;
}): JSX.Element {
  const anchor = (px: number): 'start' | 'middle' | 'end' => {
    if (px < x0 + 24) return 'start';
    if (px > x1 - 24) return 'end';
    return 'middle';
  };
  return (
    <g>
      {ticks.map((t) => {
        const px = x(t);
        return (
          <g key={`x-${t}`}>
            <line className="gd-grid" x1={px} x2={px} y1={yTop} y2={yBottom} />
            <text className="gd-axis-text" x={px} y={height - 8} textAnchor={anchor(px)}>
              {formatTimeTick(t, stepMs)}
            </text>
          </g>
        );
      })}
      <line className="gd-axis" x1={x0} x2={x1} y1={yBottom} y2={yBottom} />
    </g>
  );
}

export interface TooltipRow {
  value: string;
  label: string;
  color: string;
  dashed?: boolean;
}

export function Tooltip({ x, width, time, rows }: { x: number; width: number; time: Date; rows: TooltipRow[] }): JSX.Element {
  return (
    <div className={`gd-tooltip ${x > width * 0.6 ? 'is-left' : ''}`} style={{ left: x }} role="status" aria-live="polite">
      <div className="gd-tooltip-time">{formatDateTime(time)}</div>
      {rows.map((row) => (
        <div className="gd-tooltip-row" key={row.label}>
          <span className={`gd-key ${row.dashed ? 'is-dashed' : ''}`} style={{ '--c': row.color } as CSSProperties} aria-hidden="true" />
          <span className="gd-tooltip-value">{row.value}</span>
          <span className="gd-tooltip-label">{row.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Table-view twin of a chart. Rows render only while the disclosure is open. */
export function DataTable({ summary, columns, rows }: { summary: string; columns: { label: string; numeric?: boolean }[]; rows: () => ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <details className="gd-table" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>{summary}</summary>
      {open ? (
        <div className="gd-table-scroll">
          <table>
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.label} scope="col" className={c.numeric ? 'gd-num' : undefined}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{rows()}</tbody>
          </table>
        </div>
      ) : null}
    </details>
  );
}
